<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import type { Address } from "@types"
import { formatAddress } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================
interface Company {
    name?: string
    idcompany?: string
    legalForm?: string
    address?: Address | { inline_name?: string }
}

interface Props {
    /**
     * Informations de l'entreprise
     */
    company: Company
}

// ================================
// PROPS
// ================================
defineProps<Props>()

// ================================
// HELPERS
// ================================

/**
 * Formate l'adresse de l'entreprise en gérant le cas inline_name
 */
function getFormattedAddress(
    address?: Address | { inline_name?: string }
): string {
    if (!address) return ""

    // Si l'adresse a un inline_name, l'utiliser directement
    if ("inline_name" in address && address.inline_name) {
        return address.inline_name
    }

    // Sinon, utiliser le helper formatAddress
    return formatAddress(address as Address)
}
</script>

<template>
    <div class="flex flex-col justify-center items-start gap-0.5">
        <!-- ================================ -->
        <!-- NOM DE L'ENTREPRISE -->
        <!-- ================================ -->
        <p v-if="company.name" class="text-sm font-medium text-gray-800">
            {{ company.name }}
        </p>

        <!-- ================================ -->
        <!-- FORME JURIDIQUE + IDENTIFIANT -->
        <!-- ================================ -->
        <p v-if="company.idcompany" class="text-xs text-gray-500">
            <span v-if="company.legalForm">{{ company.legalForm }} - </span>
            n° {{ company.idcompany }}
        </p>

        <!-- ================================ -->
        <!-- ADRESSE -->
        <!-- ================================ -->
        <p
            v-if="company.address"
            class="text-xs text-gray-500 truncate max-w-full"
        >
            {{ getFormattedAddress(company.address) }}
        </p>
    </div>
</template>
